import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Lock,
    Eye,
    EyeOff,
    ArrowRight,
    ArrowLeft,
    CheckCircle2,
    AlertCircle,
    ShieldCheck,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import Logo from "../components/Logo";

const REASON_COPY = {
    not_found: {
        title: "Lien invalide",
        message:
            "Ce lien n'existe pas ou a déjà été utilisé. Refais une demande pour en recevoir un nouveau.",
    },
    already_used: {
        title: "Lien déjà utilisé",
        message:
            "Ce lien a déjà servi à réinitialiser un mot de passe. Refais une demande si nécessaire.",
    },
    expired: {
        title: "Lien expiré",
        message:
            "Ce lien n'est plus valable (validité 60 min). Refais une demande pour en recevoir un nouveau.",
    },
    account_disabled: {
        title: "Compte désactivé",
        message:
            "Ce compte est désactivé. Contacte un administrateur pour le réactiver.",
    },
    network: {
        title: "Connexion impossible",
        message:
            "On n'a pas pu vérifier ton lien. Vérifie ta connexion et recharge la page.",
    },
};

/** Inline strength meter with simple criteria reused server-side. */
function PasswordCriteria({ password }) {
    const c = [
        { ok: password.length >= 6, label: "Au moins 6 caractères" },
        { ok: /[A-Za-z]/.test(password), label: "Au moins 1 lettre" },
        { ok: /\d/.test(password), label: "Au moins 1 chiffre" },
    ];
    return (
        <ul className="space-y-1.5 mt-3" data-testid="reset-password-criteria">
            {c.map((it, i) => (
                <li
                    key={i}
                    className={`flex items-center gap-2 text-xs transition-colors ${
                        it.ok ? "text-shariti-orange" : "text-white/40"
                    }`}
                >
                    <CheckCircle2
                        className={`w-3.5 h-3.5 ${it.ok ? "opacity-100" : "opacity-30"}`}
                    />
                    {it.label}
                </li>
            ))}
        </ul>
    );
}

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();

    // checking | valid | invalid | done
    const [phase, setPhase] = useState("checking");
    const [reason, setReason] = useState(null);
    const [emailMasked, setEmailMasked] = useState("");
    const [name, setName] = useState("");

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            setPhase("invalid");
            setReason("not_found");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(
                    `/auth/reset-password/validate?token=${encodeURIComponent(token)}`,
                );
                if (cancelled) return;
                if (data?.valid) {
                    setPhase("valid");
                    setEmailMasked(data.email_masked || "");
                    setName(data.name || "");
                } else {
                    setPhase("invalid");
                    setReason(data?.reason || "not_found");
                }
            } catch (e) {
                if (cancelled) return;
                setPhase("invalid");
                setReason("network");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            toast.error("Les deux mots de passe ne correspondent pas.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", {
                token,
                password,
            });
            setPhase("done");
            toast.success("Mot de passe mis à jour ✓");
            // Auto-redirect after a short pause
            setTimeout(() => navigate("/login", { replace: true }), 2000);
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) || err.message,
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-shariti-deep-black px-6 py-10 relative overflow-hidden">
            <div className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-shariti-orange/25 blur-[120px] animate-blob" />
            <div className="absolute -bottom-40 -right-32 w-[400px] h-[400px] rounded-full bg-shariti-yellow/15 blur-[110px] animate-blob" />

            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-md"
                data-testid="reset-password-screen"
            >
                <div className="mb-8 flex justify-center">
                    <Logo
                        variant="full"
                        showAcademy
                        academySize="md"
                        className="h-20 w-auto drop-shadow-[0_0_24px_rgba(238,114,46,0.35)]"
                    />
                </div>

                {phase === "checking" && (
                    <div
                        className="glass-card rounded-2xl p-8 text-center"
                        data-testid="reset-checking"
                    >
                        <div className="w-10 h-10 border-2 border-shariti-orange border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                        <p className="text-white/55 font-light">
                            Vérification du lien…
                        </p>
                    </div>
                )}

                {phase === "invalid" && (
                    <div data-testid="reset-invalid">
                        <div className="mx-auto w-16 h-16 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center mb-6">
                            <AlertCircle className="w-9 h-9 text-red-400" />
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-3 text-center">
                            {REASON_COPY[reason]?.title || "Lien invalide"}
                        </h2>
                        <p className="text-white/55 font-light mb-8 text-center text-balance">
                            {REASON_COPY[reason]?.message ||
                                "Refais une demande pour recevoir un nouveau lien."}
                        </p>
                        <Link
                            to="/forgot-password"
                            data-testid="reset-invalid-restart"
                            className="w-full flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                        >
                            Demander un nouveau lien
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                        <Link
                            to="/login"
                            className="mt-4 flex items-center justify-center gap-2 text-sm text-white/55 hover:text-white transition-colors"
                            data-testid="reset-invalid-back-to-login"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Retour à la connexion
                        </Link>
                    </div>
                )}

                {phase === "valid" && (
                    <div data-testid="reset-form-screen">
                        <p className="text-xs font-thin tracking-[0.3em] text-shariti-orange-light uppercase mb-2 text-center">
                            Nouveau mot de passe
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-3 text-center">
                            {name ? `Bienvenue, ${name.split(" ")[0]}` : "Re-prends la main"}
                        </h2>
                        <p className="text-white/55 font-light mb-6 text-center text-balance">
                            On réinitialise le mot de passe de{" "}
                            <span className="text-white">{emailMasked || "ton compte"}</span>.
                            Choisis-en un que toi seul connais.
                        </p>

                        <form
                            onSubmit={handleSubmit}
                            className="space-y-4"
                            data-testid="reset-password-form"
                        >
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                <input
                                    type={showPwd ? "text" : "password"}
                                    placeholder="Nouveau mot de passe"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoFocus
                                    data-testid="reset-password-input"
                                    className="w-full pl-12 pr-12 py-3.5 glass-card rounded-2xl text-white placeholder-white/30 focus:outline-none focus:border-shariti-orange/60 transition-all"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-shariti-orange/15 text-shariti-orange hover:bg-shariti-orange/25 transition-colors"
                                    data-testid="reset-password-toggle"
                                    aria-label={
                                        showPwd
                                            ? "Masquer le mot de passe"
                                            : "Afficher le mot de passe"
                                    }
                                >
                                    {showPwd ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </button>
                            </div>

                            <div className="relative">
                                <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                <input
                                    type={showPwd ? "text" : "password"}
                                    placeholder="Confirme le mot de passe"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    data-testid="reset-confirm-input"
                                    className="w-full pl-12 pr-4 py-3.5 glass-card rounded-2xl text-white placeholder-white/30 focus:outline-none focus:border-shariti-orange/60 transition-all"
                                />
                            </div>

                            <PasswordCriteria password={password} />

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="reset-submit-button"
                                className="w-full flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                            >
                                {submitting ? "Mise à jour…" : "Mettre à jour"}
                                {!submitting && <ArrowRight className="w-5 h-5" />}
                            </button>
                        </form>

                        <Link
                            to="/login"
                            data-testid="reset-back-to-login"
                            className="mt-6 flex items-center justify-center gap-2 text-sm text-white/55 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Annuler
                        </Link>
                    </div>
                )}

                {phase === "done" && (
                    <div className="text-center" data-testid="reset-done">
                        <div className="mx-auto w-16 h-16 rounded-full bg-shariti-orange/20 border border-shariti-orange/40 flex items-center justify-center mb-6">
                            <CheckCircle2 className="w-9 h-9 text-shariti-orange" />
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-3">
                            C'est tout bon !
                        </h2>
                        <p className="text-white/55 font-light mb-6 text-balance">
                            Ton mot de passe a été mis à jour. On te redirige vers la
                            connexion…
                        </p>
                        <Link
                            to="/login"
                            data-testid="reset-done-go-login"
                            className="w-full flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                        >
                            Aller à la connexion
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                )}
            </motion.div>
        </div>
    );
}
